import { Button, Modal, Typography } from 'antd';
import {
  isSessionExpired,
  redirectToLogin,
  subscribeToSessionExpiry
} from 'lib/auth/sessionExpiry';
import { useEffect, useState } from 'react';

const { Paragraph } = Typography;

/**
 * The one visible consequence of an expired Auth0 session.
 *
 * Mounted once, app-wide, so any transport that detects the expiry raises it
 * without knowing where in the tree it is. It is deliberately not dismissable:
 * behind it the page still renders cached data and looks perfectly healthy,
 * which is exactly what let users retry a dead session for half an hour.
 *
 * It asks for a click rather than redirecting on its own so that whatever the
 * user was typing stays on screen — the reported case was someone mid-edit on
 * a part's UoM rows.
 */
export default function SessionExpiredModal() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // A query fired during hydration can trip the latch before this mounts, so
    // read the current value once before subscribing to later transitions.
    if (isSessionExpired()) {
      setVisible(true);
    }
    return subscribeToSessionExpiry(() => setVisible(true));
  }, []);

  return (
    <Modal
      visible={visible}
      title="Your session has expired"
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={[
        <Button key="login" type="primary" onClick={redirectToLogin}>
          Log in again
        </Button>
      ]}
    >
      <Paragraph>
        You were signed out for security. Log in again to pick up where you left
        off.
      </Paragraph>
      <Paragraph type="secondary">
        Logging in reloads this page. If you have unsaved changes on screen,
        copy them somewhere first — they were not saved.
      </Paragraph>
    </Modal>
  );
}
